import { Inbox, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'

/** Bloco cinza pulsando no lugar do conteúdo que ainda vai chegar. */
export function Esqueleto({ className }: { className?: string }) {
  return <div aria-hidden className={cn('animate-pulse rounded-xl bg-elevated', className)} />
}

export interface EsqueletoLinhasProps {
  linhas?: number
  className?: string
  /** Rótulo lido por leitores de tela. */
  rotulo?: string
}

/** Várias linhas de texto "carregando", com larguras diferentes para parecer texto. */
export function EsqueletoLinhas({
  linhas = 3,
  className,
  rotulo = 'Carregando',
}: EsqueletoLinhasProps) {
  const larguras = ['w-3/4', 'w-full', 'w-5/6', 'w-2/3', 'w-11/12']
  return (
    <div
      role="status"
      aria-label={rotulo}
      aria-busy
      className={cn('flex flex-col gap-3', className)}
    >
      {Array.from({ length: linhas }, (_, i) => (
        <Esqueleto key={i} className={cn('h-4', larguras[i % larguras.length])} />
      ))}
    </div>
  )
}

/** Cartões "rótulo + número" em carregamento (diretoria, visão geral, resumo do dia). */
export function EsqueletoCartoes({
  quantidade = 3,
  className,
}: {
  quantidade?: number
  className?: string
}) {
  return (
    <div role="status" aria-label="Carregando" aria-busy className={cn('contents', className)}>
      {Array.from({ length: quantidade }, (_, i) => (
        <Esqueleto key={i} className="h-24" />
      ))}
    </div>
  )
}

export interface SpinnerProps {
  className?: string
}

export function Spinner({ className }: SpinnerProps) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-block size-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent',
        className,
      )}
    />
  )
}

export interface EstadoVazioProps {
  titulo: string
  texto?: string | undefined
  Icone?: LucideIcon
  acao?: ReactNode
  /** `erro` troca a cor do ícone para o vermelho e anuncia como alerta. */
  tom?: 'neutro' | 'erro' | 'atencao'
  /** Tamanho do bloco: `sm` cabe numa coluna; `lg` é o do posto (toque). */
  tamanho?: 'sm' | 'md' | 'lg'
  className?: string
}

const TOM = {
  neutro: 'bg-elevated text-fg-muted',
  erro: 'bg-vermelho/15 text-vermelho',
  atencao: 'bg-amarelo/15 text-amarelo',
} as const

/**
 * Estado vazio / de erro bonito: ícone, título curto, uma frase do que fazer
 * e, se fizer sentido, a ação. Substitui "…" e textos soltos.
 */
export function EstadoVazio({
  titulo,
  texto,
  Icone = Inbox,
  acao,
  tom = 'neutro',
  tamanho = 'md',
  className,
}: EstadoVazioProps) {
  const grande = tamanho === 'lg'
  return (
    <div
      role={tom === 'erro' ? 'alert' : undefined}
      className={cn(
        'flex flex-col items-center justify-center text-center',
        tamanho === 'sm' ? 'gap-2 px-4 py-6' : grande ? 'gap-4 px-6 py-10' : 'gap-3 px-6 py-8',
        className,
      )}
    >
      <span
        className={cn(
          'grid place-items-center rounded-2xl',
          grande ? 'size-16' : tamanho === 'sm' ? 'size-10' : 'size-12',
          TOM[tom],
        )}
      >
        <Icone className={grande ? 'size-8' : tamanho === 'sm' ? 'size-5' : 'size-6'} aria-hidden />
      </span>
      <p
        className={cn(
          'font-semibold',
          grande ? 'text-2xl' : tamanho === 'sm' ? 'text-sm' : 'text-lg',
        )}
      >
        {titulo}
      </p>
      {texto && (
        <p className={cn('max-w-md text-fg-muted', grande ? 'text-lg' : 'text-sm')}>{texto}</p>
      )}
      {acao && <div className="mt-1">{acao}</div>}
    </div>
  )
}
